/**
 * Sharing Service - API calls for sharing feature
 * Handles groups, members, shared items, and user preferences
 */

import API_BASE_URL from '../config';

// ============================================================================
// TYPES
// ============================================================================

export interface SharingGroup {
  id: number;
  name: string;
  type: 'family' | 'team' | 'temporary';
  description?: string;
  created_by: number;
  created_at: string;
  expires_at?: string;
  is_active: boolean;
  role?: string;
  member_count?: number;
  item_count?: number;
}

export interface GroupMember {
  user_id: number;
  email: string;
  display_name: string;
  role: 'owner' | 'editor' | 'viewer';
  joined_at: string;
}

export interface SharedItem {
  id: number;
  name: string;
  description?: string;
  category?: string;
  area?: string;
  location?: string;
  sub_location?: string;
  quantity?: number;
  owner_user_id?: number;
  owner_group_id?: number;
  owner_name?: string;
  access_type: 'group_owned' | 'shared' | 'my_personal' | 'shared_with_me';
  created_at: string;
  shared_at?: string;
  share_notes?: string;
  user_id?: number;
  group_id?: number;
  my_role?: string;
  group_name?: string;
}

export interface CreateGroupData {
  name: string;
  type: 'family' | 'team' | 'temporary';
  description?: string;
  expires_at?: string;
}

export interface InviteMemberData {
  email: string;
  role: 'owner' | 'editor' | 'viewer';
}

export interface ShareItemsData {
  item_ids: number[];
  notes?: string;
}

// ============================================================================
// GROUP MANAGEMENT
// ============================================================================

export const createGroup = async (token: string, data: CreateGroupData): Promise<SharingGroup> => {
  console.log('sharingService: Creating group with data:', data);
  console.log('sharingService: API_BASE_URL:', API_BASE_URL);
  console.log('sharingService: Full URL:', `${API_BASE_URL}/api/sharing/groups`);
  
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  console.log('sharingService: Response status:', response.status);
  console.log('sharingService: Response ok:', response.ok);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('sharingService: Error response text:', errorText);
    let errorMessage = 'Failed to create group';
    try {
      const error = JSON.parse(errorText);
      errorMessage = error.error || errorMessage;
    } catch (e) {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  console.log('sharingService: Created group:', result);
  return result;
};

export const getUserGroups = async (token: string, type?: string): Promise<SharingGroup[]> => {
  const url = type 
    ? `${API_BASE_URL}/api/sharing/groups?type=${type}`
    : `${API_BASE_URL}/api/sharing/groups`;
  
  console.log('sharingService: Fetching groups from:', url);
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  console.log('sharingService: Response status:', response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('sharingService: Error response:', errorText);
    throw new Error(`Failed to fetch groups: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  console.log('sharingService: Groups data:', data);
  return data.groups || [];
};

export const getGroupDetails = async (token: string, groupId: number): Promise<SharingGroup & { members: GroupMember[] }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch group details');
  }

  return response.json();
};

export const updateGroup = async (token: string, groupId: number, data: Partial<CreateGroupData>): Promise<{ success: boolean; group: SharingGroup }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update group');
  }

  return response.json();
};

export const deleteGroup = async (token: string, groupId: number): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to delete group');
  }

  return response.json();
};

// ============================================================================
// MEMBER MANAGEMENT
// ============================================================================

export const inviteMember = async (token: string, groupId: number, data: InviteMemberData): Promise<{ success: boolean; member_id?: number; invitation_id?: number; status: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to invite member');
  }

  return response.json();
};

export const getGroupMembers = async (token: string, groupId: number): Promise<GroupMember[]> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/members`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch members');
  }

  const data = await response.json();
  return data.members || [];
};

export const updateMemberRole = async (token: string, groupId: number, userId: number, role: string): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/members/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ role })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update role');
  }

  return response.json();
};

export const removeMember = async (token: string, groupId: number, userId: number): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/members/${userId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove member');
  }

  return response.json();
};

// ============================================================================
// ITEM SHARING
// ============================================================================

export const shareItemsToGroup = async (token: string, groupId: number, data: ShareItemsData): Promise<{ success: boolean; shared_count: number }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to share items');
  }

  return response.json();
};

export const unshareItem = async (token: string, groupId: number, itemId: number): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/items/${itemId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to unshare item');
  }

  return response.json();
};

export const getSharedItems = async (token: string, groupId: number): Promise<SharedItem[]> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/items`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch shared items');
  }

  return response.json();
};

export const getAllGroupItems = async (token: string, groupId: number): Promise<SharedItem[]> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/items/all`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch group items');
  }

  return response.json();
};

export const getMyAllItems = async (token: string, spaceId?: number): Promise<SharedItem[]> => {
  const url = new URL(`${API_BASE_URL}/api/sharing/items/my-items`);
  if (spaceId) {
    url.searchParams.append('space_id', spaceId.toString());
  }
  
  const response = await fetch(url.toString(), {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch items');
  }

  return response.json();
};

// ============================================================================
// USER PREFERENCES
// ============================================================================

export const setDefaultGroup = async (token: string, groupId: number | null): Promise<{ success: boolean; default_group_id: number | null }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/users/default-group`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ group_id: groupId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to set default group');
  }

  return response.json();
};

export const getDefaultGroup = async (token: string): Promise<{ default_group_id: number | null }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/users/default-group`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch default group');
  }

  return response.json();
};

export const getItemsSharedGroups = async (token: string, itemIds: number[]): Promise<Record<number, Array<{ group_id: number; group_name: string; group_type: string }>>> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/items/shared-groups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ item_ids: itemIds })
  });

  if (!response.ok) {
    throw new Error('Failed to fetch items shared groups');
  }

  return response.json();
};

// ============================================================================
// ITEM SHARES (Hybrid User-User / User-Guest sharing via email)
// ============================================================================

export interface ItemShare {
  id: number;
  share_token: string;
  recipient_email: string;
  permission: 'view' | 'edit';
  message: string | null;
  created_at: string;
  expires_at: string | null;
  viewed_at: string | null;
  is_active: boolean;
  group_id: number;
  group_name: string;
  member_count: number;
  is_claimed: boolean; // true if recipient has joined the group
}

export interface ShareItemResponse {
  success: boolean;
  share_id: number;
  share_token: string;
  share_url: string;
  recipient_email: string;
  recipient_is_user: boolean;
  recipient_name: string | null;
  item_name: string;
  sharer_name: string;
  email_sent: boolean;
  created_at: string;
  expires_at: string | null;
}

export interface SharedItemData {
  share_id: number;
  item: {
    id: number;
    name: string;
    description: string | null;
    area: string | null;
    location: string | null;
    sub_location: string | null;
    quantity: number | null;
    image_url: string | null;
    thumbnail_url: string | null;
    is_grocery?: boolean;
    uit_id?: number | null;
    unit?: string | null;
    is_checked?: boolean;
  };
  sharer: {
    name: string;
    email: string;
  };
  permission: 'view' | 'edit';
  message: string | null;
  shared_at: string;
  expires_at: string | null;
  recipient_email: string;
  recipient_is_user: boolean;
}

export interface ShareItemData {
  email: string;
  permission?: 'view' | 'edit';
  message?: string;
  send_email?: boolean;
  expires_days?: number;
}

/**
 * Share an item with someone via email
 * Creates User-User share if recipient is registered, User-Guest share otherwise
 */
export const shareItemByEmail = async (
  token: string,
  itemId: number,
  data: ShareItemData
): Promise<ShareItemResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/items/${itemId}/share`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to share item');
  }

  return response.json();
};

/**
 * Get all shares for an item (only owner can see)
 */
export const getItemShares = async (
  token: string,
  itemId: number
): Promise<ItemShare[]> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/items/${itemId}/shares`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch item shares');
  }

  const data = await response.json();
  return data.shares;
};

/**
 * Revoke/delete an item share
 */
export const revokeItemShare = async (
  token: string,
  shareId: number
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/items/shares/${shareId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to revoke share');
  }

  return response.json();
};

/**
 * Get shared item data by share token (public endpoint)
 */
export const getSharedItemByToken = async (
  shareToken: string
): Promise<SharedItemData> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/s/${shareToken}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch shared item');
  }

  return response.json();
};

/**
 * Update a shared item (guest edit via share token)
 */
export const updateSharedItem = async (
  shareToken: string,
  data: {
    name?: string;
    description?: string;
    area?: string;
    location?: string;
    sub_location?: string;
    quantity?: number;
    is_grocery?: boolean;
    uit_id?: number | null;
    unit?: string | null;
    is_checked?: boolean;
  }
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/s/${shareToken}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update item');
  }

  return response.json();
};

/**
 * Claim pending shares after login/signup
 * Converts User-Guest shares to User-User shares
 */
export const claimPendingShares = async (
  token: string
): Promise<{ success: boolean; claimed_count: number }> => {
  const response = await fetch(`${API_BASE_URL}/api/sharing/claim-shares`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to claim shares');
  }

  return response.json();
};
